import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, Edit, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import dayjs from "dayjs";

export interface CompanyTableData {
  id: string;
  name: string;
  companyAdmin?: string;
  address?: string;
  phone?: string;
  createdAt: string;
  updatedAt: string;
}

interface CompaniesTableColumnsProps {
  onEdit: (company: CompanyTableData) => void;
  onDelete: (company: CompanyTableData) => void;
}

export const companiesTableColumns = ({
  onEdit,
  onDelete,
}: CompaniesTableColumnsProps): ColumnDef<CompanyTableData>[] => [
  {
    accessorKey: "name",
    header: () => (
      <Button variant="ghost">
        Company Name
      </Button>
    ),
    cell: ({ row }) => (
      <div className="font-medium">{row.getValue("name")}</div>
    ),
  },
  {
    accessorKey: "companyAdmin",
    header: () => (
      <Button variant="ghost">
        Company Admin
      </Button>
    ),
    cell: ({ row }) => {
      const admin = row.getValue("companyAdmin") as string;
      return (
        <div className="text-sm">
          {admin || (
            <Badge variant="secondary">No Admin</Badge>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "address",
    header: () => (
      <Button variant="ghost">
        Address
      </Button>
    ),
    cell: ({ row }) => {
      const address = row.getValue("address") as string;
      return (
        <div className="text-sm max-w-[200px] truncate">
          {address || "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "phone",
    header: () => (
      <Button variant="ghost">
        Phone
      </Button>
    ),
    cell: ({ row }) => {
      const phone = row.getValue("phone") as string;
      return (
        <div className="text-sm">
          {phone || "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "createdAt",
    header: () => (
      <Button variant="ghost">
        Created At
      </Button>
    ),
    cell: ({ row }) => {
      const date = row.getValue("createdAt") as string;
      return (
        <div className="text-sm">
          {dayjs(date).format("MM/DD/YYYY h:mm A")}
        </div>
      );
    },
  },
  {
    accessorKey: "updatedAt",
    header: () => (
      <Button variant="ghost">
        Updated At
      </Button>
    ),
    cell: ({ row }) => {
      const date = row.getValue("updatedAt") as string;
      return (
        <div className="text-sm">
          {dayjs(date).format("MM/DD/YYYY h:mm A")}
        </div>
      );
    },
  },
  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      const company = row.original;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => navigator.clipboard.writeText(company.id)}
            >
              Copy company ID
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onEdit(company)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit company
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onDelete(company)}
              className="text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete company
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
